/**
 * Preference Validation Utilities
 * 
 * Validation functions for account preferences form
 * Extracted from AccountSettingsPage for testability
 */

import type { UpdatePreferencesInput } from '../types/preference.types';

export interface ValidationErrors {
  [key: string]: string;
}

const VALID_PAY_FREQUENCIES = ['weekly', 'fortnightly', 'monthly'] as const;

/**
 * Validate preference form data
 * Returns validation errors object and boolean indicating if form is valid
 */
export function validatePreferencesForm(formData: UpdatePreferencesInput): {
  isValid: boolean;
  errors: ValidationErrors;
} {
  const errors: ValidationErrors = {};

  if (formData.currency && !['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'].includes(formData.currency)) {
    errors.currency = 'Invalid currency selection';
  }

  if (formData.dateFormat && !['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'DDD MMM DD YYYY'].includes(formData.dateFormat)) {
    errors.dateFormat = 'Invalid date format selection';
  }

  if (formData.financialYearStart && !['January', 'April', 'July', 'October'].includes(formData.financialYearStart)) {
    errors.financialYearStart = 'Invalid financial year start selection';
  }

  if (formData.theme && !['light', 'dark', 'auto'].includes(formData.theme)) {
    errors.theme = 'Invalid theme selection';
  }

  if (formData.transactionRetentionYears !== undefined && 
      formData.transactionRetentionYears !== null && 
      ![1, 2, 5, 10].includes(formData.transactionRetentionYears)) {
    errors.transactionRetentionYears = 'Invalid retention period selection';
  }

  // Pay Day fields — all null / frequency unset is valid (opt-in)
  if (formData.payFrequency !== undefined && formData.payFrequency !== null) {
    if (!VALID_PAY_FREQUENCIES.includes(formData.payFrequency)) {
      errors.payFrequency = 'Invalid pay frequency selection';
    } else {
      if (formData.payDay === undefined || formData.payDay === null) {
        errors.payDay = 'Pay day is required when pay frequency is set';
      } else if (formData.payFrequency === 'monthly') {
        if (!Number.isInteger(formData.payDay) || formData.payDay < 1 || formData.payDay > 31) {
          errors.payDay = 'Pay day must be between 1 and 31 for monthly pay';
        }
      } else {
        // weekly / fortnightly — ISO weekday 1 (Mon) … 7 (Sun)
        if (!Number.isInteger(formData.payDay) || formData.payDay < 1 || formData.payDay > 7) {
          errors.payDay = 'Pay day must be a weekday between Monday (1) and Sunday (7)';
        }
      }

      if (formData.payFrequency === 'fortnightly') {
        if (!formData.payAnchorDate) {
          errors.payAnchorDate = 'Anchor date is required for fortnightly pay';
        }
      }
    }
  }

  if (formData.estimatedNetPay !== undefined && formData.estimatedNetPay !== null) {
    if (typeof formData.estimatedNetPay !== 'number' || Number.isNaN(formData.estimatedNetPay) || formData.estimatedNetPay <= 0) {
      errors.estimatedNetPay = 'Estimated net pay must be greater than zero';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
